"use client";

import { useErrorBoundary, type FallbackProps } from "react-error-boundary";

import { logError } from "#app/lib/logger";
import { Banner } from "#ui/banner";
import { Button } from "#ui/button";
import { Card, CardContent, type CardRootProps } from "#ui/card";
import { Container } from "#ui/container";
import { Icon } from "#ui/icon";
import { Link, LinkButton } from "#ui/link";
import { Main } from "#ui/main";
import { H1, P } from "#ui/typography";

export function ErrorFallbackContent({
  className,
  description,
  error,
  onClickRetry,
  title,
  ...props
}: Omit<CardRootProps, "children"> & {
  description?: string;
  error: unknown;
  onClickRetry: (...args: unknown[]) => void;
  title?: string;
}) {
  logError(error);

  return (
    <Card
      {...props}
      variant="white-border-and-blur"
      className={className}
      role="alert"
    >
      <CardContent>
        <div
          // Same classNames as PendingCardContent - Keep in sync!
          className="grid place-content-center place-items-center gap-20px text-center md:min-h-280px md:py-30px"
        >
          <Icon
            name="baird-maintenance-circle"
            size="baird-lg"
            palette="warning"
            className="mb-5px"
          />
          <H1 size="h4" className="leading-none">
            {title ?? "Something went wrong"}
          </H1>
          {description ? <p>{description}</p> : null}
          <P className="text-balance">
            The page could not be loaded due to an unexpected system problem.
            Please refresh the page or try again later. If the issue continues,
            contact{" "}
            <Link href="/contact-us" className="whitespace-nowrap">
              Baird Online Support
            </Link>
            .
          </P>
          <div className="grid w-full gap-10px @sm:max-w-400px @sm:grid-cols-2">
            <Button onClick={() => onClickRetry()}>Try again</Button>
            <LinkButton href="/" variant="outline">
              Return Home
            </LinkButton>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ErrorFallback({ error }: Pick<FallbackProps, "error">) {
  const { resetBoundary } = useErrorBoundary();

  return (
    <Main>
      <Banner className="-mb-60px md:-mb-80px xl:-mb-108px">
        <Container />
      </Banner>
      <Container>
        <ErrorFallbackContent
          error={error}
          onClickRetry={resetBoundary}
          className="m-auto max-w-600px"
        />
      </Container>
    </Main>
  );
}
